import { useCallback } from 'react'

import { toast } from 'sonner'

import { ButtonDropdownOption } from '~/components/component-library'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import {
  AddToBatchStatus,
  useAddToTransactionBatch,
} from '~/hooks/useTransactionBatch'
import {
  BackendTransactionCall,
  BasicTransactionType,
  BatchOperation,
  MetadataPayloads,
  OrgTransaction,
} from '~/types'
import { buildBatchOperation } from '~/utils/smartAccounts/batch'

const ADD_TO_BATCH_SHORTCUT = {
  key: 'meta+shift+enter',
  enableOnFormTags: true,
}

export const buildAddToBatchDropdownOptions = ({
  canAddToBatch,
  isDisabled,
  onClick,
}: {
  canAddToBatch: AddToBatchStatus
  isDisabled: boolean
  onClick: () => unknown
}): ButtonDropdownOption[] => [
  {
    label: 'Add to batch',
    onClick: () => void onClick(),
    isDisabled: isDisabled || !canAddToBatch.canAdd,
    disabledReason: canAddToBatch.canAdd
      ? undefined
      : canAddToBatch.disabledReason,
    shortcut: ADD_TO_BATCH_SHORTCUT,
  },
]

export const useAddCurrentFormToBatch = ({
  onSuccess,
}: {
  onSuccess?: (args: {
    batch: OrgTransaction
    addedOperation: BatchOperation | undefined
  }) => void
} = {}) => {
  const { addToBatch, isProcessing } = useAddToTransactionBatch()

  const addCurrentFormToBatch = useCallback(
    async ({
      smartAccountId,
      chainId,
      title,
      types,
      calls,
      metadata,
      memo,
      walletConnectAppName,
      walletConnectAppDomain,
      setIsOpen,
    }: {
      smartAccountId: string
      chainId: number
      title: string
      types: BasicTransactionType[]
      calls: BackendTransactionCall[]
      metadata: MetadataPayloads[]
      memo?: string
      walletConnectAppName?: string
      walletConnectAppDomain?: string
      setIsOpen: (isOpen: boolean) => void
    }) => {
      const batch = await addToBatch({
        smartAccountId,
        chainId,
        operation: buildBatchOperation({
          name: title,
          types,
          calls,
          walletConnectAppName,
          walletConnectAppDomain,
        }),
        metadata,
        memo,
      })

      if (!batch) return

      const addedOperation = batch.batchOperations?.at(-1)
      toast.success('Transaction added to batch', {
        description: addedOperation ? (
          <TransactionTitle
            transaction={{
              titleChunks: addedOperation.titleChunks,
              chainId: batch.chainId,
            }}
            unstyled
          />
        ) : (
          title
        ),
      })

      setIsOpen(false)
      onSuccess?.({ batch, addedOperation })
    },
    [addToBatch, onSuccess],
  )

  return { addCurrentFormToBatch, isProcessing }
}
